import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { useState } from "react";

interface AddDayControlProps {
  existingOffsets: number[];
  onAdd: (offsetDays: number) => void;
}

/** "Day [ N ] + Add day" — any whole number of days, unlimited count; a duplicate is refused inline. */
export function AddDayControl({ existingOffsets, onAdd }: AddDayControlProps): React.ReactElement {
  const [value, setValue] = useState<string>("");
  const parsed = value.trim() === "" ? null : Number.parseInt(value, 10);
  const invalid = parsed === null || Number.isNaN(parsed) || parsed < 0;
  const duplicate = parsed !== null && existingOffsets.includes(parsed);

  const handleAdd = (): void => {
    if (parsed === null || invalid || duplicate) return;
    onAdd(parsed);
    setValue("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <span className="body-sm text-grey-900">Day</span>
      <input
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="e.g. 30"
        className="body-sm w-20 rounded-sm border border-grey-400 px-2 py-1.5 text-center"
        aria-label="New day number"
      />
      <Button variant={ButtonVariant.DASHED} size={ButtonSize.SMALL} disabled={invalid || duplicate} onClick={handleAdd}>
        + Add day
      </Button>
      <span className="body-xs text-grey-600">
        {duplicate ? `Day ${parsed} already exists.` : "Any number of days, no limit. Days are sorted by number."}
      </span>
    </div>
  );
}
